/** 原始 FFT 峰值（0~1）低于此值视为无音乐，不做峰值归一化 */
export const RHYTHM_RAW_PEAK_THRESHOLD = 0.02;

/** 综合振幅低于此值视为静音 */
export const RHYTHM_ACTIVE_AMPLITUDE_THRESHOLD = 0.025;

export function rawFrequencyPeak(
  frequencyData: number[] | null | undefined
): number {
  if (!frequencyData?.length) return 0;
  let peak = 0;
  for (let i = 0; i < frequencyData.length; i += 1) {
    const v = (frequencyData[i] ?? 0) / 255;
    if (v > peak) peak = v;
  }
  return peak;
}

/** 从 FFT bins 估算归一化振幅（0~1），桌面端偶发 currentAmplitude=0 但 bins 有能量时使用 */
export function deriveRhythmAmplitudeFromFrequencyData(
  frequencyData: number[] | null | undefined
): number {
  if (!frequencyData?.length) return 0;
  let sum = 0;
  for (let i = 0; i < frequencyData.length; i += 1) {
    sum += frequencyData[i] ?? 0;
  }
  return Math.min(1, sum / (frequencyData.length * 255));
}

export type RhythmActivityInput = {
  frequencyData?: number[] | null;
  currentAmplitude?: number | null;
  rms?: number | null;
  rhythmSpectrum?: number[] | null;
};

/** 是否有足够能量驱动律动（停止播放/静音时应为 false） */
export function isRhythmInputActive(input: RhythmActivityInput): boolean {
  const { frequencyData, currentAmplitude, rms } = input;
  if (!frequencyData?.length) return false;

  const rawPeak = rawFrequencyPeak(frequencyData);
  if (rawPeak < RHYTHM_RAW_PEAK_THRESHOLD) return false;

  const amp = Math.max(
    Math.max(0, Number(currentAmplitude) || 0),
    Math.max(0, Number(rms) || 0),
    deriveRhythmAmplitudeFromFrequencyData(frequencyData)
  );
  if (amp < RHYTHM_ACTIVE_AMPLITUDE_THRESHOLD) return false;

  return true;
}

/**
 * 合并宿主上报振幅与 FFT 推导值。
 * 部分 Electron 版本首帧/全程 currentAmplitude=0，但 frequencyData 正常，会导致律动判定静音。
 */
export function resolveEffectiveRhythmAmplitude(
  frequencyData: number[] | null | undefined,
  reportedAmplitude: number | null | undefined
): number {
  if (!frequencyData?.length) return 0;
  if (rawFrequencyPeak(frequencyData) < RHYTHM_RAW_PEAK_THRESHOLD) return 0;

  const reported = Math.max(0, Math.min(1, Number(reportedAmplitude) || 0));
  if (reported >= 0.02) return reported;
  const derived = deriveRhythmAmplitudeFromFrequencyData(frequencyData);
  return Math.max(reported, derived);
}
